import { EventActions } from '../actions'
import { ApiError } from '../api-error'
import type { SelectOption } from '../components/forms/select/select'
import type {
    ProductVariantProps,
    RowProps,
    TicketsTableProps,
} from '../components/tickets-table/tickets-table'
import { handleError } from '../handle-error'
import type { TicketsSectionProps } from '../modules/checkout/tickets-section'
import type {
    AppliedMemberCard,
    EventView,
    GiftCardPayment,
    SerialTicketPayment,
    TicketGroup,
    TicketSeat,
    TicketType,
} from '../types'
import { CheckoutStep } from '../types'
import { formatPrice } from '../utils/format-price'
import { handleGtmAddToCart } from '../utils/handle-gtm-add-to-cart'
import { debouncedSetProductQuantity } from './product-utils'

const getSerialTicketRowProps = (view: EventView, payment: SerialTicketPayment): RowProps => {
    const { lang } = view.state

    return {
        reactKey: `serialTicket-${payment.serialTicketId}`,
        title: lang.tickets_serialTicket.replace('{number}', payment.number),
        price: formatPrice(-payment.amount),
        onRemove: async () => {
            try {
                await EventActions.removeSerialTicket(view, payment.serialTicketId)
            } catch (error) {
                handleError(view, error as Error)
            }
        },
    }
}

const getMemberCardRowProps = (view: EventView, card: AppliedMemberCard): RowProps => ({
    reactKey: 'memberCard',
    title: `${card.name} ${card.number}`,
    price: formatPrice(-card.discount),
    noteLines: card.description ? [card.description] : [],
    onRemove: async () => {
        try {
            await EventActions.removeCard(view, card.number)
        } catch (error) {
            handleError(view, error as Error)
        }
    },
})

const getGiftCardRowProps = (view: EventView, payment: GiftCardPayment): RowProps => {
    const { state } = view
    const { lang } = state

    return {
        reactKey: `giftCard-${payment.number}`,
        title: lang.tickets_giftCard.replace('{number}', payment.number),
        subtitle: lang.tickets_amountLeft + ' ' + formatPrice(payment.amountLeft - payment.amount),
        price: formatPrice(-payment.amount),
        onRemove: async () => {
            try {
                await EventActions.removeCard(view, payment.number)
            } catch (error) {
                handleError(view, error as Error)
            }
        },
    }
}

const getSeatRowProps = (view: EventView, group: TicketGroup, seat: TicketSeat): RowProps => {
    const { state } = view
    const { ticketTypes } = state
    const { typeUuid, unitPrice } = group
    const { rowNumber, seatNumber, allowedTicketTypeIds } = seat

    const props: RowProps = {
        reactKey: `seat-${rowNumber},${seatNumber}`,
        isTicket: true,
        title: seat.name,
        price: formatPrice(unitPrice),
        select: {
            id: `seat-${rowNumber}-${seatNumber}`,
            value: typeUuid,
            onChange: async (newTicketType) => {
                try {
                    await EventActions.changeSeatType(view, rowNumber, seatNumber, newTicketType)
                } catch (error) {
                    handleError(view, error as Error)
                }
            },
            options: ticketTypes
                .filter((type) => allowedTicketTypeIds.includes(type.id))
                .map(
                    (type): SelectOption => ({
                        value: type.id,
                        name: type.name,
                    }),
                ),
        },
        onRemove: async () => {
            try {
                await EventActions.removeSeat(view, rowNumber, seatNumber)
            } catch (error) {
                handleError(view, error as Error)
            }
        },
    }

    return props
}

const getTicketTypeRowProps = (view: EventView, ticketType: TicketType): RowProps => {
    const { state, update } = view

    const groups = state.calc.tickets.filter((group) => group.typeUuid === ticketType.id)
    const quantity = groups.reduce((acc, group) => acc + group.quantity, 0)
    const totalPrice = groups.reduce((acc, group) => acc + group.unitPrice * group.quantity, 0)

    const props: RowProps = {
        reactKey: `ticket-type-${ticketType.id}`,
        isTicket: true,
        title:
            ticketType.name + (ticketType.price ? ' (' + formatPrice(ticketType.price) + ')' : ''),
        price: formatPrice(totalPrice),
        numberField: {
            id: `ticket-${ticketType.id}`,
            value: quantity,
            onChange: async (newValue) => {
                try {
                    await EventActions.setTicketAmount(view, ticketType.id, newValue)
                    state.isTicketLimit = false
                    update()

                    if (newValue < quantity) {
                        return
                    }

                    const toAdd = newValue - quantity

                    handleGtmAddToCart(
                        ticketType.name,
                        ticketType.id,
                        ticketType.price.toString(),
                        toAdd.toString(),
                    )
                } catch (error) {
                    const errorCode = error instanceof ApiError ? error.code : null

                    if (errorCode === 'maximum-tickets-per-session') {
                        state.isTicketLimit = true
                        update()
                    } else {
                        handleError(view, error as Error)
                    }
                }
            },
            min: 0,
        },
    }

    if (groups.some((group) => group.unitPrice !== ticketType.price)) {
        props.noteLines = groups.map(
            (group) =>
                `${group.quantity} x ${group.name}: ${formatPrice(group.unitPrice * group.quantity)}`,
        )
    }

    return props
}

const getTable = (view: EventView): TicketsTableProps => {
    const { state, update } = view
    const { lang, isAddProductsFlow, isProductSaleEnabled, activeCheckoutStep, calc } = state

    const rows: RowProps[] = []
    const table: TicketsTableProps = { rows }

    if (
        !isAddProductsFlow &&
        (activeCheckoutStep === CheckoutStep.TICKETS || activeCheckoutStep === CheckoutStep.SUMMARY)
    ) {
        if (state.floorplan) {
            for (const group of calc.tickets) {
                for (const seat of group.seats!) {
                    rows.push(getSeatRowProps(view, group, seat))
                }
            }
        } else {
            for (const ticketType of state.ticketTypes) {
                rows.push(getTicketTypeRowProps(view, ticketType))
            }
        }
    }

    if (
        activeCheckoutStep === CheckoutStep.PRODUCTS ||
        activeCheckoutStep === CheckoutStep.SUMMARY
    ) {
        for (const product of state.products) {
            const variants = product.variants
                .filter((variant) => variant.quantity > 0)
                .map(
                    (variant): ProductVariantProps => ({
                        id: variant.id,
                        name: variant.name,
                        quantity: {
                            id: `variant-${variant.id}`,
                            value: variant.quantity,
                            onChange: async (newValue) => {
                                debouncedSetProductQuantity(view, variant.id, newValue)
                                variant.quantity = newValue
                                update()
                            },
                            min: 0,
                            max: variant.maxQuantity ?? 99,
                        },
                        price: formatPrice(variant.unitPrice * variant.quantity),
                    }),
                )

            if (variants.length) {
                rows.push({
                    reactKey: `product-${product.id}`,
                    isProduct: true,
                    title: product.name,
                    variants,
                    onRemove: async () => {
                        for (const variant of product.variants) {
                            debouncedSetProductQuantity(view, variant.id, 0)
                            variant.quantity = 0
                        }

                        update()
                    },
                })
            }
        }
    }

    if (!isProductSaleEnabled || activeCheckoutStep === CheckoutStep.SUMMARY) {
        for (const payment of calc.serialTicketPayments) {
            rows.push(getSerialTicketRowProps(view, payment))
        }

        if (calc.memberCard) {
            rows.push(getMemberCardRowProps(view, calc.memberCard))
        }

        for (const payment of calc.giftCardPayments) {
            rows.push(getGiftCardRowProps(view, payment))
        }

        table.total = {
            title: lang.tickets_total,
            sum: formatPrice(calc.toPay),
        }
    }

    return table
}

export const getTicketsSection = (view: EventView): TicketsSectionProps => {
    const { state } = view
    const { lang, activeCheckoutStep, isAddProductsFlow } = state

    const ticketsSection: TicketsSectionProps = {
        error: state.isTicketLimit ? lang.tickets_max : undefined,
        table: getTable(view),
    }

    if (activeCheckoutStep === CheckoutStep.TICKETS) {
        ticketsSection.title = lang.tickets_title
        ticketsSection.text = state.labels.tickets.subtitle
    }

    if (activeCheckoutStep === CheckoutStep.PRODUCTS) {
        ticketsSection.title = lang.products_title
    }

    if (activeCheckoutStep === CheckoutStep.SUMMARY) {
        ticketsSection.title = lang.cart_title

        if (!isAddProductsFlow) {
            ticketsSection.text = state.labels.tickets.subtitle
        } else {
            ticketsSection.text = ''
        }
    }

    return ticketsSection
}
